import ElstrLanguageService from "./services/ElstrLanguageService";
import ElstrLanguageServiceBE2 from "./services/be2/ElstrLanguageService";
import { languageExists } from "./ElstrApplicationData";
import ElstrBaseApp from "./ElstrBaseApp";
import { ELSTR_BACKEND_VERSION } from "./constants/BackendVersion";
import { filterXSS } from "xss";

declare global {
  interface Window {
    ELSTR: any;
  }
}

function convertUnicode(input): string {
  return input.replace(/\\u(\w\w\w\w)/g, function(a, b) {
    const charcode = parseInt(b, 16);
    return String.fromCharCode(charcode);
  });
}

function finishDic(translatedString: string): string {
  // convert unicode with convertUnicode
  // sanitizing translation-strings with filterXSS
  return filterXSS(convertUnicode(translatedString));
}

/**
 * Used to translate a given key
 * Will return the key when the current translations object doesn't have they key
 * Optional replacer arguments can be provided to replace %s in translation, converted to a string
 */
export function dic(key: string, ...replacer: any[]): string {
  try {
    if (ElstrLanguage.getTranslations()[key] && replacer.length === 0) {
      return finishDic(ElstrLanguage.getTranslations()[key]);
    }

    if (ElstrLanguage.getTranslations()[key] && replacer.length >= 1) {
      const originalTranslation = ElstrLanguage.getTranslations()[key];

      if (originalTranslation.split("%s").length === 1) {
        // no %s in the text
        return originalTranslation;
      }

      let replacedTranslation: string = originalTranslation;

      replacer.forEach(fragment => {
        replacedTranslation = replacedTranslation.replace("%s", fragment);
      });

      return finishDic(replacedTranslation);
    }

    if (ElstrLanguage.showErrors) {
      console.warn(
        `Translation-Key (${key}) not found in dictionary within language (${ElstrLanguage.activeLang})`,
      );
    }
  } catch (e) {
    console.error(e);
  }

  return key;
}

/**
 * Short function to return the active language
 * @return {string}
 */
export function activeLang(): string {
  return ElstrLanguage.activeLang;
}

/**
 * Short function to return the set active language
 */
export function setActiveLang(lang: string) {
  ElstrLanguage.setActiveLang(lang).then();
}

export function setActiveLangNoRerender(lang: string) {
  ElstrLanguage.setActiveLangNoRerender(lang).then();
}

/**
 * This is the class for Language used in Elstr projects (JSON data strings)
 *
 * @class ElstrLanguage
 */
export default class ElstrLanguage {
  private static reRender: any;
  private static _activeLang: string;
  private static translations: object;

  public static showErrors = true;

  constructor(reRender) {
    ElstrLanguage.reRender = reRender;

    const _languageExists = languageExists();
    ElstrLanguage._activeLang = _languageExists
      ? window.ELSTR.applicationData.language.current
      : "de";
    ElstrLanguage.translations = _languageExists
      ? window.ELSTR.applicationData.language.translations
      : {};

    ElstrLanguage.updateHtmlLang();

    // Delete data from window object
    if (_languageExists) {
      window.ELSTR.applicationData.language = null;
    }
  }

  static get activeLang(): string {
    return this._activeLang;
  }

  disableErrors() {
    ElstrLanguage.showErrors = false;
  }

  static async setActiveLang(lang: string) {
    await ElstrLanguage.handleSetActiveLang(lang);
    ElstrLanguage.reRender();
  }

  static async setActiveLangNoRerender(lang: string) {
    await ElstrLanguage.handleSetActiveLang(lang);
  }

  static async setTranslations(translations: object) {
    ElstrLanguage.translations = translations;
  }

  static async handleSetActiveLang(lang: string) {
    await ElstrLanguage.loadLanguageFromService(lang);
    ElstrLanguage.updateHtmlLang();
  }

  static getTranslations(): object {
    return ElstrLanguage.translations;
  }

  static async loadLanguageFromService(langCode: string) {
    const oldLang = ElstrLanguage._activeLang;

    try {
      ElstrLanguage._activeLang = langCode;

      if (ElstrBaseApp.ELSTR_BACKEND_VERSION === ELSTR_BACKEND_VERSION.BE1) {
        ElstrLanguage.translations = await ElstrLanguageService.load(langCode);
      }

      if (ElstrBaseApp.ELSTR_BACKEND_VERSION === ELSTR_BACKEND_VERSION.BE2) {
        const result = await ElstrLanguageServiceBE2.getAllMessages();
        ElstrLanguage.translations = result?.data?.messages || {};
      }
    } catch (e) {
      console.group("Setting new Language");
      console.error("new language could not be set.");
      ElstrLanguage._activeLang = oldLang;
      console.error("oldLang remains activeLang.");
      console.info("server msg is:");
      console.error(e);
      console.groupEnd();
    }
  }

  static updateHtmlLang() {
    document.documentElement.lang = ElstrLanguage._activeLang;
  }
}
